// ========================================
// RATE LIMITING UTILITIES
// Debounce and throttle functions for API calls
// ========================================

import { HTTP_CONFIG } from './httpConfig';

/**
 * Creates a debounced function that delays invoking func until after wait milliseconds
 * have elapsed since the last time the debounced function was invoked.
 * 
 * @param {Function} func - The function to debounce
 * @param {number} wait - The number of milliseconds to delay (default: SEARCH_DEBOUNCE_MS)
 * @param {Object} options - Options object
 * @param {boolean} options.leading - Invoke on the leading edge (default: false)
 * @param {boolean} options.trailing - Invoke on the trailing edge (default: true)
 * @param {number} options.maxWait - Maximum time to wait before invoking
 * @returns {Function} The debounced function with cancel() method
 * 
 * @example
 * const debouncedSearch = debounce(searchAPI, 400);
 * input.addEventListener('input', (e) => debouncedSearch(e.target.value));
 * // Clean up: debouncedSearch.cancel();
 */
export function debounce(func, wait = HTTP_CONFIG.RATE_LIMIT.SEARCH_DEBOUNCE_MS, options = {}) {
  const { leading = false, trailing = true, maxWait } = options;
  
  let timeoutId = null;
  let lastCallTime = null;
  let lastInvokeTime = 0;
  let lastArgs = null;
  let lastThis = null;
  let result;

  const hasMaxWait = maxWait !== undefined;
  const maxWaitMs = hasMaxWait ? Math.max(maxWait, wait) : undefined;

  function invokeFunc(time) {
    const args = lastArgs;
    const thisArg = lastThis;
    lastArgs = lastThis = null;
    lastInvokeTime = time;
    result = func.apply(thisArg, args);
    return result;
  }

  function shouldInvoke(time) {
    const timeSinceLastCall = lastCallTime === null ? 0 : time - lastCallTime;
    const timeSinceLastInvoke = time - lastInvokeTime;

    return (
      lastCallTime === null ||
      timeSinceLastCall >= wait ||
      timeSinceLastCall < 0 ||
      (hasMaxWait && timeSinceLastInvoke >= maxWaitMs)
    );
  }

  function remainingWait(time) {
    const timeSinceLastCall = time - lastCallTime;
    const timeSinceLastInvoke = time - lastInvokeTime;
    const timeWaiting = wait - timeSinceLastCall;

    return hasMaxWait
      ? Math.min(timeWaiting, maxWaitMs - timeSinceLastInvoke)
      : timeWaiting;
  }

  function timerExpired() {
    const time = Date.now();
    if (shouldInvoke(time)) {
      return trailingEdge(time);
    }
    timeoutId = setTimeout(timerExpired, remainingWait(time));
  }

  function trailingEdge(time) {
    timeoutId = null;
    if (trailing && lastArgs) {
      return invokeFunc(time);
    }
    lastArgs = lastThis = null;
    return result;
  }

  function leadingEdge(time) {
    lastInvokeTime = time;
    timeoutId = setTimeout(timerExpired, wait);
    return leading ? invokeFunc(time) : result;
  }

  function cancel() {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }
    lastInvokeTime = 0;
    lastArgs = lastCallTime = lastThis = timeoutId = null;
  }

  function flush() {
    if (timeoutId === null) {
      return result;
    }
    return trailingEdge(Date.now());
  }

  function pending() {
    return timeoutId !== null;
  }

  function debounced(...args) {
    const time = Date.now();
    const isInvoking = shouldInvoke(time);

    lastArgs = args;
    lastThis = this;
    lastCallTime = time;

    if (isInvoking) {
      if (timeoutId === null) {
        return leadingEdge(time);
      }
      if (hasMaxWait) {
        timeoutId = setTimeout(timerExpired, wait);
        return invokeFunc(time);
      }
    }

    if (timeoutId === null) {
      timeoutId = setTimeout(timerExpired, wait);
    }

    return result;
  }

  debounced.cancel = cancel;
  debounced.flush = flush;
  debounced.pending = pending;

  return debounced;
}

/**
 * Creates a throttled function that only invokes func at most once per every wait milliseconds.
 * 
 * @param {Function} func - The function to throttle
 * @param {number} wait - The number of milliseconds to throttle (default: SUBMIT_THROTTLE_MS)
 * @param {Object} options - Options object
 * @param {boolean} options.leading - Invoke on the leading edge (default: true)
 * @param {boolean} options.trailing - Invoke on the trailing edge (default: true)
 * @returns {Function} The throttled function with cancel() method
 * 
 * @example
 * const throttledSubmit = throttle(handleSubmit, 2000);
 * form.addEventListener('submit', throttledSubmit);
 * // Clean up: throttledSubmit.cancel();
 */
export function throttle(func, wait = HTTP_CONFIG.RATE_LIMIT.SUBMIT_THROTTLE_MS, options = {}) {
  const { leading = true, trailing = true } = options;
  return debounce(func, wait, { leading, trailing, maxWait: wait });
}

/**
 * Creates an async debounced function that returns a promise
 * Useful for debouncing API calls in async contexts
 * 
 * @param {Function} func - The async function to debounce
 * @param {number} wait - The number of milliseconds to delay
 * @returns {Function} The debounced async function
 * 
 * @example
 * const debouncedSearch = debounceAsync(searchAPI, 400);
 * const results = await debouncedSearch(query);
 */
export function debounceAsync(func, wait = HTTP_CONFIG.RATE_LIMIT.SEARCH_DEBOUNCE_MS) {
  let timeoutId = null;
  let pendingPromise = null;
  let resolve = null;
  let reject = null;

  function debounced(...args) {
    // Cancel previous timeout
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }

    // Create new promise if none pending
    if (!pendingPromise) {
      pendingPromise = new Promise((res, rej) => {
        resolve = res;
        reject = rej;
      });
    }

    // Set new timeout
    timeoutId = setTimeout(async () => {
      try {
        const result = await func.apply(this, args);
        resolve(result);
      } catch (error) {
        reject(error);
      } finally {
        pendingPromise = null;
        resolve = null;
        reject = null;
        timeoutId = null;
      }
    }, wait);

    return pendingPromise;
  }

  debounced.cancel = () => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    if (reject) {
      reject(new Error('Debounced call cancelled'));
    }
    pendingPromise = null;
    resolve = null;
    reject = null;
  };

  return debounced;
}

/**
 * Creates a function that can only be called once within the specified time window
 * Subsequent calls within the window will return the same promise
 * 
 * @param {Function} func - The async function to deduplicate
 * @param {number} wait - The time window in milliseconds
 * @returns {Function} The deduplicated function
 * 
 * @example
 * const deduplicatedFetch = deduplicateAsync(fetchData, 1000);
 * // Multiple rapid calls will only execute once:
 * await Promise.all([deduplicatedFetch(), deduplicatedFetch(), deduplicatedFetch()]);
 */
export function deduplicateAsync(func, wait = 1000) {
  let lastCallTime = 0;
  let pendingPromise = null;

  return async function (...args) {
    const now = Date.now();
    
    // If we have a pending promise and we're within the dedup window, return it
    if (pendingPromise && now - lastCallTime < wait) {
      return pendingPromise;
    }

    lastCallTime = now;
    pendingPromise = func.apply(this, args);

    try {
      return await pendingPromise;
    } finally {
      // Clear pending promise after completion
      setTimeout(() => {
        if (Date.now() - lastCallTime >= wait) {
          pendingPromise = null;
        }
      }, wait);
    }
  };
}

/**
 * React hook for debounced value
 * @param {*} value - The value to debounce
 * @param {number} delay - Delay in milliseconds
 * @returns {*} The debounced value
 * 
 * @example
 * function SearchComponent() {
 *   const [query, setQuery] = useState('');
 *   const debouncedQuery = useDebounce(query, 400);
 *   
 *   useEffect(() => {
 *     if (debouncedQuery) searchAPI(debouncedQuery);
 *   }, [debouncedQuery]);
 * }
 */
export function createUseDebounce(useState, useEffect) {
  return function useDebounce(value, delay = HTTP_CONFIG.RATE_LIMIT.SEARCH_DEBOUNCE_MS) {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);

      return () => {
        clearTimeout(handler);
      };
    }, [value, delay]);

    return debouncedValue;
  };
}

/**
 * Creates a rate limiter that tracks API call counts
 * Useful for showing rate limit warnings to users
 * 
 * @param {number} maxCalls - Maximum calls allowed
 * @param {number} windowMs - Time window in milliseconds
 * @returns {Object} Rate limiter object
 * 
 * @example
 * const limiter = createRateLimiter(5, 60000); // 5 calls per minute
 * if (!limiter.canMakeCall()) {
 *   showWarning('Too many requests, please wait');
 * }
 */
export function createRateLimiter(maxCalls, windowMs) {
  const calls = [];

  return {
    canMakeCall() {
      const now = Date.now();
      // Remove calls outside the window
      while (calls.length > 0 && calls[0] < now - windowMs) {
        calls.shift();
      }
      return calls.length < maxCalls;
    },

    recordCall() {
      calls.push(Date.now());
    },

    getRemainingCalls() {
      const now = Date.now();
      while (calls.length > 0 && calls[0] < now - windowMs) {
        calls.shift();
      }
      return Math.max(0, maxCalls - calls.length);
    },

    getTimeUntilReset() {
      if (calls.length === 0) return 0;
      const oldestCall = calls[0];
      return Math.max(0, windowMs - (Date.now() - oldestCall));
    },

    reset() {
      calls.length = 0;
    },
  };
}
